import { coreQueryKey } from '@/api/core/authorization';
import { readNativePropertyContent } from '@/api/core/native-property-content';
import { LoadFailure } from '@/components/load-failure';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { invalidateHomepagePreview } from './homepage-preview-cache';
import { NativeFaqManager } from './native-faq-manager';
import { FaqSharedEditor } from './native-faq-shared-editor';
import { propertyRead, type PropertyRuntime } from './property-queries';

export function NativePropertyFaq({
  runtime,
  propertyId,
  generation,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  generation: number;
}) {
  const [open, setOpen] = useState(false);
  const cache = useQueryClient();
  const faqKey = [
    ...coreQueryKey(runtime.apiNamespace, generation, 'native-property-catalog', propertyId),
    'faq',
  ];
  const query = useQuery({
    queryKey: faqKey,
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        generation,
        () => readNativePropertyContent(runtime.client, propertyId, signal),
        signal,
        () => runtime.session.getSnapshot().generation === generation,
      ),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  return (
    <section className="space-y-4 rounded-md border p-4">
      <h2 className="font-semibold">지점 FAQ</h2>
      <p className="body-sm text-muted-foreground">
        현재 배정된 FAQ · 페이지별 저장 설정 (실제 게시 상태와 별개)
      </p>
      {!open && (
        <Button
          variant="outline"
          onClick={() => {
            setOpen(true);
          }}
        >
          FAQ 확인
        </Button>
      )}
      {open && query.isFetching && (
        <LoadingAnnouncement visible>FAQ를 불러오고 있습니다.</LoadingAnnouncement>
      )}
      {open && query.isError && (
        <LoadFailure subject="FAQ" error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.isSuccess && (
        <>
          {!query.data.faq_sets.length && <p>현재 이 지점에 배정된 FAQ가 없습니다.</p>}
          {query.data.faq_sets.map((set) => (
            <section className="space-y-3 border-t pt-4" key={set.assignment_id}>
              <h3 className="font-medium">
                {set.name ?? '분류명 없음'} · {set.language}
              </h3>
              {!set.items.length && <p>이 배정에 선택된 FAQ 항목이 없습니다.</p>}
              {set.items.map((item) => (
                <article key={item.id} className="space-y-3 rounded-md border p-4">
                  {item.title !== null && <h4 className="font-medium">{item.title}</h4>}
                  {(['ko', 'en'] as const).map((language) => (
                    <dl key={language} className="body-sm space-y-2">
                      <div className="space-y-1">
                        <dt className="text-muted-foreground">질문 · {language}</dt>
                        <dd className="break-words whitespace-pre-wrap">
                          {item.payload.question[language] ?? '원문 없음'}
                        </dd>
                      </div>
                      <div className="space-y-1">
                        <dt className="text-muted-foreground">답변 · {language}</dt>
                        <dd className="break-words whitespace-pre-wrap">
                          {item.payload.answer[language] ?? '원문 없음'}
                        </dd>
                      </div>
                    </dl>
                  ))}
                  <p className="body-sm text-muted-foreground">
                    지점 상세: {item.payload.detail ? '표시' : '숨김'} · 신청 완료:{' '}
                    {item.payload.apply_completed ? '표시' : '숨김'} · 빈방 알림:{' '}
                    {item.payload.vacant_room ? '표시' : '숨김'}
                  </p>
                  <FaqSharedEditor
                    runtime={runtime}
                    propertyId={propertyId}
                    collectionId={set.collection_id}
                    item={item}
                    onSaved={() => {
                      void cache.invalidateQueries({ queryKey: faqKey, exact: true });
                      void invalidateHomepagePreview(cache);
                    }}
                  />
                </article>
              ))}
            </section>
          ))}
        </>
      )}
      {open && (
        <NativeFaqManager
          runtime={runtime}
          propertyId={propertyId}
          onChanged={() => {
            void cache.invalidateQueries({ queryKey: faqKey, exact: true });
            void invalidateHomepagePreview(cache);
          }}
        />
      )}
    </section>
  );
}
